<script setup lang="ts">
import type { HouseDetails } from '~/types/house/types'
import type { HousePhotoAttributes } from 'lc-services/types'

const {
  house,
  photos = [],
  virtualVisit,
} = defineProps<{
  house: HouseDetails
  photos?: HousePhotoAttributes[]
  virtualVisit?: string
}>()
defineEmits<{ 'open-slider': [string] }>()

const rangePhotos = computed(() =>
  [photos[0]]
    .concat(
      virtualVisit
        ? [
            {
              id: 'virtual-visit',
              hidden: false,
              position: 2,
              url: virtualVisit,
            },
          ]
        : [],
    )
    .concat(photos.slice(1))
    .reduce((acc, photo, index) => {
      const range = Math.floor(index / 8)
      if (!Array.isArray(acc[range])) acc.push([])
      acc[range].push(photo)
      return acc
    }, [] as HousePhotoAttributes[][]),
)
</script>

<template>
  <div
    v-for="(range, rangeIndex) in rangePhotos"
    :key="`${rangeIndex}-key`"
    class="house-grid"
  >
    <div
      v-for="photo in range"
      :key="photo.id"
      :class="['house-cell', { 'sm:!row-span-2': photo.position === 1 }]"
      @click="$emit('open-slider', photo.id)"
    >
      <div
        v-if="photo.id === 'virtual-visit'"
        class="relative size-full cursor-pointer"
      >
        <video autoplay class="size-full object-cover" loop muted playsinline>
          <source :src="virtualVisit" type="video/mp4" />
        </video>
        <p
          class="absolute bottom-2 left-2 mb-0 w-fit text-nowrap rounded bg-gray-700/50 px-3 py-2 text-md text-white sm:bottom-4 sm:left-4 sm:mb-2"
        >
          {{ $t('house.virtualVisit.title') }}
        </p>
      </div>
      <BaseNuxtImg
        v-else
        class="scale-1 size-full cursor-pointer transition duration-500 ease-in-out sm:hover:scale-[1.015]"
        :alt="house.name"
        sizes="xs:100vw sm:80vw xxl:75vw"
        :src="photo.url"
        @click="$emit('open-slider', photo.id)"
      />
    </div>
  </div>
</template>

<style scoped>
.house-grid {
  @apply grid w-full grid-cols-2 grid-rows-[repeat(auto-fit,85px)] gap-2 px-4;
}
.house-cell {
  @apply flex overflow-hidden min-h-[85px] max-h-[170px];
}
/* Mobile grid */
.house-cell:only-child {
  @apply min-h-[275px];
}
.house-cell:first-child {
  @apply max-h-[228px] col-span-2 row-start-1 row-span-3;
}
.house-cell:nth-child(2) {
  @apply col-span-1 row-start-4 row-span-1;
}
.house-cell:nth-child(3) {
  @apply col-start-2 col-span-1 row-start-4 row-span-1;
}
.house-cell:nth-child(4) {
  @apply col-start-1 col-span-2 row-start-5 row-span-1;
}
.house-cell:nth-child(5) {
  @apply col-start-1 col-span-1 row-start-6 row-span-1;
}
.house-cell:nth-child(6) {
  @apply col-start-2 col-span-1 row-start-6 row-span-1;
}
.house-cell:nth-child(7) {
  @apply col-start-1 col-span-1 row-start-7 row-span-1;
}
.house-cell:nth-child(8) {
  @apply col-start-2 col-span-1 row-start-7 row-span-1;
}
/** Specific case for a grid with 2 pictures */
.house-grid:has(> :last-child:nth-child(2)) .house-cell:nth-child(2) {
  @apply max-h-[228px] col-span-2 row-span-3;
}

@screen sm {
  .house-grid {
    @apply grid-cols-4 gap-4 px-0;
  }
  .house-grid:not(:last-child) {
    @apply pb-2;
  }
  .house-cell,
  .house-cell:first-child {
    max-height: unset;
  }
  /* 8 photos (default grid) */
  .house-grid:has(> :last-child:nth-child(8)) {
    @apply grid-rows-[repeat(5,296px)];
  }
  .house-grid:has(> :last-child:nth-child(8)) .house-cell:first-child {
    @apply col-span-4 row-span-1;
  }
  .house-cell:nth-child(2) {
    @apply col-span-1 row-span-1;
  }
  .house-cell:nth-child(3) {
    @apply col-span-1 row-span-1;
  }
  .house-cell:nth-child(4) {
    @apply col-span-2 row-span-2;
  }
  .house-cell:nth-child(5) {
    @apply col-span-2  row-span-1;
  }
  .house-cell:nth-child(6) {
    @apply col-span-2  row-span-2;
  }
  .house-cell:nth-child(7) {
    @apply col-span-2  row-span-1;
  }
  .house-cell:nth-child(8) {
    @apply col-span-2  row-span-1;
  }
  /* 1 photo */
  .house-grid:has(> :last-child:nth-child(1)) {
    @apply grid-rows-[608px];
  }
  .house-grid:has(> :last-child:nth-child(1)) .house-cell {
    @apply col-start-1 col-span-4 row-span-1 max-h-[608px];
  }
  /* 2 photos */
  .house-grid:has(> :last-child:nth-child(2)) {
    @apply grid-rows-[608px];
  }
  .house-grid:has(> :last-child:nth-child(2)) .house-cell:first-child {
    @apply col-start-1 col-span-2 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(2)) .house-cell:nth-child(2) {
    max-height: unset;
    @apply col-start-3 col-span-2 row-span-1;
  }
  /* 3 photos */
  .house-grid:has(> :last-child:nth-child(3)) {
    @apply grid-rows-[repeat(2,304px)];
  }
  .house-grid:has(> :last-child:nth-child(3)) .house-cell:first-child {
    @apply col-span-2 row-span-2;
  }
  .house-grid:has(> :last-child:nth-child(3)) .house-cell:nth-child(2) {
    @apply col-span-2 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(3)) .house-cell:nth-child(3) {
    @apply col-span-2 row-span-1;
  }
  /* 4 photos */
  .house-grid:has(> :last-child:nth-child(4)) {
    @apply grid-rows-[repeat(2,304px)];
  }
  .house-grid:has(> :last-child:nth-child(4)) .house-cell:first-child {
    @apply col-span-2 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(4)) .house-cell:nth-child(2) {
    @apply col-start-3 col-span-2 row-start-1 row-span-2;
  }
  .house-grid:has(> :last-child:nth-child(4)) .house-cell:nth-child(3) {
    @apply col-start-1 col-span-1 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(4)) .house-cell:nth-child(4) {
    @apply col-start-2 col-span-1 row-span-1;
  }
  /* 5 photos */
  .house-grid:has(> :last-child:nth-child(5)) {
    @apply grid-rows-[repeat(3,304px)];
  }
  .house-grid:has(> :last-child:nth-child(5)) .house-cell:first-child {
    @apply col-span-2 row-span-2;
  }
  .house-grid:has(> :last-child:nth-child(5)) .house-cell:nth-child(2) {
    @apply col-start-3 col-span-2 row-start-1 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(5)) .house-cell:nth-child(3) {
    @apply col-start-3 col-span-1 row-start-2 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(5)) .house-cell:nth-child(4) {
    @apply col-start-4 col-span-1 row-start-2 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(5)) .house-cell:nth-child(5) {
    @apply col-span-4 row-span-1;
  }
  /* 6 photos */
  .house-grid:has(> :last-child:nth-child(6)) {
    @apply grid-rows-[repeat(4,304px)];
  }
  .house-grid:has(> :last-child:nth-child(6)) .house-cell:first-child {
    @apply col-span-2 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(6)) .house-cell:nth-child(2) {
    @apply col-start-3 col-span-2 row-start-1 row-span-2;
  }
  .house-grid:has(> :last-child:nth-child(6)) .house-cell:nth-child(3) {
    @apply col-start-1 col-span-2 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(6)) .house-cell:nth-child(4) {
    @apply col-start-1 col-span-2 row-span-2;
  }
  .house-grid:has(> :last-child:nth-child(6)) .house-cell:nth-child(5) {
    @apply col-start-3 col-span-2 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(6)) .house-cell:nth-child(6) {
    @apply col-start-3 col-span-2 row-start-4 row-span-1;
  }
  /* 7 photos */
  .house-grid:has(> :last-child:nth-child(7)) {
    @apply grid-rows-[repeat(5,304px)];
  }
  .house-grid:has(> :last-child:nth-child(7)) .house-cell:first-child {
    @apply col-span-2 row-span-2;
  }
  .house-grid:has(> :last-child:nth-child(7)) .house-cell:nth-child(2) {
    @apply col-start-3 col-span-2 row-start-1 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(7)) .house-cell:nth-child(3) {
    @apply col-start-3 col-span-2 row-start-2 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(7)) .house-cell:nth-child(4) {
    @apply col-start-1 col-span-4 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(7)) .house-cell:nth-child(5) {
    @apply col-start-1 col-span-2 row-start-4 row-span-1;
  }
  .house-grid:has(> :last-child:nth-child(7)) .house-cell:nth-child(6) {
    @apply col-start-3 col-span-2 row-start-4 row-span-2;
  }
  .house-grid:has(> :last-child:nth-child(7)) .house-cell:nth-child(7) {
    @apply col-start-1 col-span-2 row-start-5 row-span-1;
  }
}
</style>
